import React from 'react';
import { ConvertStringToHtmlId } from '../../libs/ts/ConvertStringToHtmlId';
import { FieldValues, Path, useFormContext, get } from 'react-hook-form';
import { FormComponentProps } from '../types';
interface FormTextInputProps<T> extends FormComponentProps<T> {
  title: string;
  placeholder?: string;
  mandatory?: boolean;
  className?: string;
  type?: string;
  hideErrorControl?: boolean;
}

export const FormTextInput = <T extends FieldValues>(props: FormTextInputProps<T>) => {
  const {
    register,
    formState: { errors }
  } = useFormContext<T>();

  const errorForName = React.useMemo(() => {
    return get(errors, props.name);
  }, [errors, props.name]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const inputProps = register<Path<T>>(props.name, {
    setValueAs: (value?: string | null) => value && `${value}`.trim()
  });
  const inputRef = React.createRef<HTMLInputElement>();

  const onClick = () => {
    setFocus();
  };

  const setFocus = () => {
    inputRef?.current?.focus();
  };

  const backgroundColour = () => {
    if (props.readonly) {
      return 'bg-transparent';
    } else if (errorForName) {
      return 'bg-feedback-dark-red';
    } else {
      return 'bg-interactive-bold-green-db-12';
    }
  };

  const borderColour = () => {
    if (props.readonly) {
      return 'border-transparent';
    } else if (errorForName) {
      return 'border-b-feedback-red';
    } else {
      return 'border-b-solid-bold-green';
    }
  };

  const labelTextColour = () => {
    if (props.readonly) {
      return 'text-solid-white';
    } else if (errorForName) {
      return 'text-feedback-red';
    } else {
      return 'text-solid-bold-green';
    }
  };

  const errorControl = () => {
    const error = errorForName;
    if (!error) {
      return <br />;
    }
    return <label className="text-feedback-red text-xs">{error.message?.toString()}</label>;
  };

  return (
    <div className={`w-full ${props.className}`}>
      <div
        className={`cursor-text ${backgroundColour()} ${borderColour()} pl-3 border-b-2 pt-2 pb-2 text-solid-white placeholder-shown:text-secondary-deep-blue-30`}
        onClick={onClick}
      >
        <div className="flex flex-row justify-between pr-3">
          <label className={`cursor-text text-xs ${labelTextColour()} block truncate`}>{props.title}</label>
          {props.mandatory && !props.readonly && (
            <label className={`text-xs ${labelTextColour()} block`}>*</label>
          )}
        </div>
        <input
          {...inputProps}
          readOnly={props.readonly}
          // ref={inputRef}
          id={ConvertStringToHtmlId(props.title)}
          className={`${backgroundColour()} focus:outline-none w-full`}
          type={props.type ?? 'text'}
          placeholder={props.placeholder}
        />
      </div>
      {!props.hideErrorControl && <div>{errorControl()}</div>}
    </div>
  );
};
